'use client';
import { PlusIcon } from '@heroicons/react/24/outline';
import { IndustriesItemType } from '@lib/sanity/queries/modules/industries/types';
import Text, { TextVariant } from '@ui/Text/Text';
import cn from '@utils/cn';
import { useId, useState } from 'react';

type IndustriesAccordionProps = {
  items: IndustriesItemType[];
};

// One row open at a time. Every panel stays in the HTML, so closed rows are still indexed;
// `invisible` takes them out of the accessibility tree once the height transition ends.
export default function IndustriesAccordion({ items }: IndustriesAccordionProps) {
  const [openKey, setOpenKey] = useState<string>();
  const baseId = useId();

  function renderItem(item: IndustriesItemType) {
    const isOpen = openKey === item._key;
    const panelId = `${baseId}-${item._key}`;
    const chips = item.chips?.filter((chip) => chip.label?.value);

    return (
      <li key={item._key} className="border-b">
        <h3>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => setOpenKey(isOpen ? undefined : item._key)}
            className="group text-foreground flex w-full cursor-pointer items-center justify-between gap-4 py-6 text-left md:py-8"
          >
            <Text
              as="span"
              variant={TextVariant.TitleLG}
              content={item.name?.value}
              className={cn(
                'transition-[color,translate] duration-500 group-hover:translate-x-[0.12em] motion-reduce:transition-none',
                {
                  'text-accent-foreground': isOpen,
                },
              )}
            />
            <span
              aria-hidden
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-full border lg:size-11',
                'group-hover:bg-foreground group-hover:text-background',
                'transition-[rotate,background-color,color] duration-500 motion-reduce:transition-none',
                {
                  'bg-foreground text-background rotate-45': isOpen,
                  'group-hover:rotate-90': !isOpen,
                },
              )}
            >
              <PlusIcon className="size-4 lg:size-5" />
            </span>
          </button>
        </h3>
        <div
          id={panelId}
          className={cn(
            'grid transition-[grid-template-rows,visibility] duration-500 ease-[cubic-bezier(.16,1,.3,1)]',
            'motion-reduce:transition-none',
            {
              'grid-rows-[1fr]': isOpen,
              'invisible grid-rows-[0fr]': !isOpen,
            },
          )}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-6 pb-6 md:flex-row md:items-end md:justify-between md:gap-10 md:pb-8">
              <Text
                as="p"
                variant={TextVariant.TextBodyLG}
                content={item.description?.value}
                className="max-w-[46ch]"
              />
              {chips?.length ? (
                <ul className="flex flex-wrap gap-2">
                  {chips.map((chip) => (
                    <li key={chip._key} className="rounded-full border px-3.5 py-1">
                      <Text as="span" variant={TextVariant.TextBodySM} content={chip.label?.value} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      </li>
    );
  }

  return <ul className="border-t">{items.map(renderItem)}</ul>;
}
